class FrCarouselSwap extends window.BAO.CustomElement() {
  static get requiredElements() {
    return ["skeleton", "slot"];
  }

  connectedCallback() {
    super.connectedCallback();

    this.stripHideMarkers();
    this.hideMarkerObserver = new MutationObserver(() =>
      this.stripHideMarkers(),
    );
    this.hideMarkerObserver.observe(this.sectionScope, {
      subtree: true,
      attributes: true,
      attributeFilter: ["data-tms-hide-on-slot-load"],
    });

    if (this.isPopulated) {
      this.revealWhenReady();
      return;
    }

    this.slotObserver = new MutationObserver(() => {
      window.clearTimeout(this.revealDelayTimeoutId);
      this.revealDelayTimeoutId = window.setTimeout(() => {
        if (!this.isPopulated) return;

        this.slotObserver.disconnect();
        this.revealWhenReady();
      }, this.revealDelay);
    });

    this.slotObserver.observe(this.els.slot.element, {
      childList: true,
      subtree: true,
    });

    this.setupMock();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.slotObserver?.disconnect();
    this.hideMarkerObserver?.disconnect();
    window.clearTimeout(this.revealDelayTimeoutId);
    window.clearTimeout(this.mockTimeoutId);
  }

  get sectionScope() {
    return this.closest("section") ?? this;
  }

  stripHideMarkers() {
    this.sectionScope
      .querySelectorAll("[data-tms-hide-on-slot-load]")
      .forEach((el) => el.removeAttribute("data-tms-hide-on-slot-load"));
  }

  async revealWhenReady() {
    await Promise.race([
      this.whenImagesLoaded(),
      this.timeout(this.imageTimeout),
    ]);

    await this.doubleAnimationFrame();

    this.els.slot.element.removeAttribute("inert");
    this.els.skeleton.element.setAttribute("inert", "");

    this.setAttribute("data-swap-ready", "");
    this.setAttribute("aria-busy", "false");

    this.dispatchEvent(
      new CustomEvent("fr-carousel-swap:ready", { bubbles: true }),
    );
  }

  get isPopulated() {
    const slot = this.els.slot.element;
    const mount = slot.querySelector(".tmspslot");

    if (mount) {
      if (mount.childElementCount > 0 || mount.textContent.trim().length > 0) {
        return true;
      }

      return slot.children.length > 1;
    }

    return slot.childElementCount > 0;
  }

  async whenImagesLoaded() {
    const images = Array.from(this.els.slot.element.querySelectorAll("img"));

    await Promise.all(images.map((image) => this.whenImageLoaded(image)));
  }

  whenImageLoaded(image) {
    if (image.complete && image.naturalWidth > 0) {
      return typeof image.decode === "function"
        ? image.decode().catch(() => {})
        : Promise.resolve();
    }

    return new Promise((resolve) => {
      image.addEventListener("load", () => resolve(), { once: true });
      image.addEventListener("error", () => resolve(), { once: true });
    });
  }

  timeout(duration) {
    return new Promise((resolve) => {
      window.setTimeout(resolve, duration);
    });
  }

  doubleAnimationFrame() {
    return new Promise((resolve) => {
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => resolve());
      });
    });
  }

  setupMock() {
    const params = new URLSearchParams(window.location.search);

    if (!params.has("fr_mock")) return;

    const delay = Number(params.get("fr_mock_delay")) || 1500;

    this.mockTimeoutId = window.setTimeout(() => {
      const mount = this.els.slot.element.querySelector(".tmspslot");
      const target = mount || this.els.slot.element;

      target.innerHTML = this.mockHtml;
    }, delay);
  }

  get mockHtml() {
    const cards = Array.from({ length: 5 }, (_, index) =>
      this.mockCard(index),
    ).join("");

    return `<div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(150px,1fr));gap:30px;padding-inline:var(--Grid_Gutter,20px);">${cards}</div>`;
  }

  mockCard(index) {
    const hue = (index * 67) % 360;
    const image =
      "data:image/svg+xml," +
      encodeURIComponent(
        `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000 1132"><rect width="1000" height="1132" fill="hsl(${hue} 45% 82%)"/><text x="500" y="580" font-family="sans-serif" font-size="64" text-anchor="middle" fill="hsl(${hue} 45% 30%)">Mock ${index + 1}</text></svg>`,
      );

    return `
      <div style="display:flex;flex-direction:column;gap:10px;">
        <img
          src="${image}"
          alt="Mock product ${index + 1}"
          width="1000"
          height="1132"
          style="display:block;width:100%;height:auto;border-radius:3px;"
        >
        <span style="font-size:16px;">12 people bought this today</span>
        <span style="font-size:16px;line-height:1.5;min-height:2.4em;">Mock product ${index + 1}</span>
        <span style="font-size:14px;line-height:1.4;min-height:2.8em;">A mock description used to test the Fresh Relevance swap locally.</span>
        <span style="font-size:16px;padding-block-start:10px;border-top:1px solid #3333331a;">£19.99</span>
      </div>
    `;
  }

  get revealDelay() {
    return 200;
  }

  get imageTimeout() {
    return 3000;
  }
}

if (!customElements.get("fr-carousel-swap")) {
  customElements.define("fr-carousel-swap", FrCarouselSwap);
}
